import { PageSection, ResponsiveQAIssue, SiteData } from '../types';

/**
 * Runs a comprehensive heuristic responsive audit on all sections in the active site
 */
export function runSiteResponsiveAudit(siteData: SiteData): ResponsiveQAIssue[] {
  const issues: ResponsiveQAIssue[] = [];

  if (!siteData || !siteData.pages) return issues;

  siteData.pages.forEach((page) => {
    (page?.sections || []).forEach((section, index) => {
      const style = section.style || {};
      const content = section.content || {};
      const sectionName = section.type ? `${section.type} (#${index + 1})` : `Bölüm #${index + 1}`;

      // 1. Column Overflow Check
      if (style.columns && style.columns >= 4) {
        if (!style.mobileOverrides?.columns) {
          issues.push({
            id: `col-overflow-${section.id}`,
            sectionId: section.id,
            sectionTitle: sectionName,
            severity: 'warning',
            device: 'mobile',
            title: '4+ Kolonlu Yapı Mobilde Sıkışabilir',
            description: `Masaüstünde ${style.columns} kolon kullanılmış. Mobilde otomatik tek kolona (1 kolon) dönüştürülmesi önerilir.`,
            autoFixAvailable: true,
            fixActionKey: 'fix_columns',
          });
        }
      }

      // 2. Padding/Spacious Height Check on Mobile
      if (style.sectionHeight === 'spacious' && !style.mobileOverrides?.sectionHeight) {
        issues.push({
          id: `height-spacious-${section.id}`,
          sectionId: section.id,
          sectionTitle: sectionName,
          severity: 'info',
          device: 'mobile',
          title: 'Geniş Boşluk Mobilde Fazla Kaydırmaya Yol Açabilir',
          description: 'Geniş (spacious) dikey dolgu mobilde gereksiz uzun boşluklar yaratabilir. Mobilde "Normal" veya "Kompakt" önerilir.',
          autoFixAvailable: true,
          fixActionKey: 'fix_padding',
        });
      }

      // 3. Long title or unspaced text check
      if (content.title && typeof content.title === 'string') {
        const words = content.title.split(' ');
        const hasLongWord = words.some((w) => w.length > 25);
        if (hasLongWord) {
          issues.push({
            id: `long-word-${section.id}`,
            sectionId: section.id,
            sectionTitle: sectionName,
            severity: 'critical',
            device: 'mobile',
            title: 'Uzun Kesintisiz Kelime Mobilde Taşmaya Neden Olabilir',
            description: 'Başlıkta boşluksuz çok uzun bir kelime tespit edildi. Küçük ekranlarda ekran dışına taşabilir.',
            autoFixAvailable: true,
            fixActionKey: 'fix_long_text',
          });
        }
      }

      // 4. Container Width Check
      if (style.containerWidth === 'compact' && !style.mobileOverrides?.containerWidth) {
        // Just fine, but on mobile it should be full width
      }
    });
  });

  return issues;
}

/**
 * Realtime browser DOM inspector to detect actual elements overflowing horizontally
 */
export function scanDomForHorizontalOverflow(rootElement?: HTMLElement | null): {
  hasOverflow: boolean;
  overflowingElements: Array<{ tag: string; id?: string; className: string; scrollWidth: number; clientWidth: number }>;
} {
  const container = rootElement || document.body;
  if (!container) return { hasOverflow: false, overflowingElements: [] };

  const overflowingElements: Array<{
    tag: string;
    id?: string;
    className: string;
    scrollWidth: number;
    clientWidth: number;
  }> = [];

  const elements = container.querySelectorAll('*');
  const viewportWidth = container.clientWidth || window.innerWidth;

  elements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    // Check if element is wider than parent or viewport and causes scroll
    if (htmlEl.scrollWidth > htmlEl.clientWidth + 2 && htmlEl.scrollWidth > viewportWidth) {
      overflowingElements.push({
        tag: htmlEl.tagName.toLowerCase(),
        id: htmlEl.id || undefined,
        className: htmlEl.className ? String(htmlEl.className).slice(0, 50) : '',
        scrollWidth: htmlEl.scrollWidth,
        clientWidth: htmlEl.clientWidth,
      });
    }
  });

  return {
    hasOverflow: overflowingElements.length > 0,
    overflowingElements: overflowingElements.slice(0, 10),
  };
}

/**
 * Automatically applies standard responsive best-practices to a section
 */
export function autoFixSectionResponsive(section: PageSection, actionKey?: string): PageSection {
  const updated = { ...section, style: { ...(section.style || {}) } };
  const currentMobile = { ...(updated.style.mobileOverrides || {}) };

  switch (actionKey) {
    case 'fix_columns':
      currentMobile.columns = 1;
      currentMobile.mobileStack = true;
      break;

    case 'fix_padding':
      currentMobile.sectionHeight = 'normal';
      break;

    case 'fix_long_text':
      // Ensure mobile typography is slightly scaled down
      currentMobile.headingFontSize = 'text-2xl sm:text-3xl';
      break;

    default:
      // Apply all standard responsive best-practices
      currentMobile.columns = 1;
      currentMobile.mobileStack = true;
      currentMobile.buttonWidth = 'full';
      currentMobile.sectionHeight = 'normal';
      break;
  }

  updated.style.mobileOverrides = currentMobile;
  return updated;
}
